import { captureException, addBreadcrumb } from "@web-monorepo/telemetry";
import {
  InfiniteData,
  Query,
  QueryKey,
  InvalidateQueryFilters,
  InvalidateOptions,
  QueryFilters,
  SetDataOptions,
  CancelOptions,
} from "@tanstack/react-query";
import { APIResponseError } from "@web-monorepo/infra/responseHandlers";
import { produce } from "@web-monorepo/hooks";
import isEqual from "fast-deep-equal";
import { useCallback, useMemo } from "react";
import { type ApiMethod, type ApiPath, APIResponse, EndpointParams, EndpointQueryParameters } from "./types";
import { InvalidCollectionResponseError, makeApiErrorMessage } from "./error";
import { useInfiniteQuery, UseInfiniteQueryOptions, InfiniteQueryObserverResult } from "./hacks";
import { queryClient } from "./queryClient";
import { registerQuery, makeQueryKey } from "./queryKey";
import { mergeFns, useQueryScope } from "./queryUtils";
import { buildUrl } from "./urlBuilder";
import { buildHeaders } from "./headersBuilder";
import { urlPatternFromFetcher } from "./urlPatternFromFetcher";
import { NOOP, WAITING_FOR_DEPENDENCIES, CUSTOM_HEADERS } from "./constants";
import { handleSafariSpecialCase } from "./safariSpecialCase";
import { apiFetch } from "./apiFetch";

function chunk<T>(arr: T[], size: number): T[][] {
  if (size < 1) return [];
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
  return result;
}

type CollectionQueryParams<
  Path extends ApiPath,
  QueryParams extends keyof EndpointQueryParameters<Path>,
> = EndpointParams<Path, QueryParams> & {
  [CUSTOM_HEADERS]?: Record<string, string>;
};
type FullCollectionQueryParams<Params> = Params | typeof NOOP | typeof WAITING_FOR_DEPENDENCIES;

// React Query options exposed at the configuration level. Some options are
// facilitated by makeCollectionQuery and some would cause unexpected behavior with
// the internal logic.
type ExposedUseQueryOptions<MemberType> = Omit<
  // Technically this should be UseInfiniteQueryOptions, but: https://github.com/TanStack/query/issues/3065.
  // For now, this doesn't really matter as we wouldn't be exposing any infinite query options anyway.
  UseInfiniteQueryOptions<DojoCollectionPage<MemberType>, APIResponseError, MemberType[]>,
  | "queryKey"
  | "queryFn"
  | "enabled"
  | "meta"
  | "queryKeyHashFn"
  | "useErrorBoundary"
  | "select"
  | "onSuccess"
  | "onError"
  | "onSettled"
  | "getNextPageParam"
  | "defaultPageParam"
>;

type EnhancedUseQueryConfig<MemberType, Params> = ExposedUseQueryOptions<MemberType> & {
  onSuccess?: (data: MemberType[], params: Params) => void;
  onError?: (error: Error, params: Params) => void;
  onSettled?: (data: MemberType[] | undefined, error: Error | null, params: Params) => void;
  select?: (data: MemberType[]) => MemberType[];
  enabled?: boolean;
};

type EnhancedUseQueryOptions<MemberType, Params> = Omit<EnhancedUseQueryConfig<MemberType, Params>, "select">;

type CollectionQueryData<Path extends ApiPath> =
  "get" extends ApiMethod<Path>
    ? APIResponse<Path, "get"> extends { _items: unknown[] }
      ? APIResponse<Path, "get">["_items"][number]
      : never
    : never;

export interface CollectionQueryType<
  Path extends ApiPath,
  QueryParams extends keyof EndpointQueryParameters<Path>,
  MemberType = CollectionQueryData<Path>,
  Params = CollectionQueryParams<Path, QueryParams>,
> {
  (
    params: FullCollectionQueryParams<Params>,
    options?: EnhancedUseQueryOptions<MemberType, Params>,
  ): InfiniteQueryObserverResult<MemberType[]>;

  invalidateQueries(
    params?: Partial<CollectionQueryParams<Path, QueryParams>>,
    filters?: Omit<InvalidateQueryFilters, "queryKey">,
    options?: InvalidateOptions,
  ): Promise<void>;

  shouldInvalidateQueries(
    shouldInvalidateQuery: (
      data: MemberType[] | undefined,
      params: CollectionQueryParams<Path, QueryParams>,
    ) => boolean | null | undefined,
  ): void;

  cancelQueries(
    params?: Partial<CollectionQueryParams<Path, QueryParams>>,
    filters?: Omit<QueryFilters, "queryKey">,
    options?: CancelOptions,
  ): Promise<void>;

  getQueryData(params?: CollectionQueryParams<Path, QueryParams>): MemberType[] | undefined;

  getQueriesData(
    params?: Partial<CollectionQueryParams<Path, QueryParams>>,
    filters?: Omit<InvalidateQueryFilters, "queryKey">,
  ): [QueryKey, MemberType[] | undefined][];

  setQueriesData(
    updater: ((draft: MemberType[]) => MemberType[] | void) | MemberType[] | null,
    params?: Partial<CollectionQueryParams<Path, QueryParams>>,
    filters?: Omit<QueryFilters, "queryKey">,
    options?: SetDataOptions,
  ): void;

  /**
   * Imperatively warms the cache for `params`' first page outside of React render, e.g.
   * from a router loader. Shares the exact query key the hook uses, so a subsequent hook
   * call with the same params is served from cache instead of refetching. Never throws or
   * rejects — a failed prefetch just leaves the cache empty and the hook fetches normally.
   */
  prefetch(params: Params): Promise<void>;
}

export interface DojoCollectionPage<T = unknown> {
  _items: T[];
  _links?: {
    prev: { href: string };
    next: { href: string };
  };
  _metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

const flattenInfiniteData = <T>(data: InfiniteData<DojoCollectionPage<T>> | undefined, fetcherName?: string): T[] => {
  const flattened = data?.pages.flatMap(({ _items }) => _items) ?? [];

  // Single pass to count undefined and null items for performance
  let undefinedCount = 0;
  let nullCount = 0;
  for (const item of flattened) {
    if (item === undefined) undefinedCount++;
    if (item === null) nullCount++;
  }

  if (undefinedCount > 0 || nullCount > 0) {
    captureException(new Error("flattenInfiniteData found undefined or null items in collection"), {
      extra: {
        fetcherName,
        pagesCount: data?.pages.length,
        totalItems: flattened.length,
        undefinedCount,
        nullCount,
      },
    });
  }

  return flattened;
};

const unflattenCollection = <T>(
  collection: T[] | null,
  data?: InfiniteData<DojoCollectionPage<T>>,
): InfiniteData<DojoCollectionPage<T>> | undefined => {
  if (!data) {
    // If there is no data, no fetches have been completed from the server. However, if there is a collection
    // that means that member(s) were inserted client-side. In that case, return cache data that represents a
    // single "fetched" page, but no page param because we don't know where to fetch it from. With the pageParam
    // being undefined, subsequent fetches will use the firstPageUrl.
    return collection
      ? {
          pages: [{ _items: collection }],
          pageParams: [undefined],
        }
      : undefined;
  }

  const numPagesLoaded = data.pages.length;
  const itemsPerPage = Math.ceil((collection?.length ?? 0) / numPagesLoaded);

  const chunks = chunk(collection ?? [], itemsPerPage);
  const pages = data.pages.map((page, index) => ({
    ...page,
    _items: chunks[index] ?? [],
  }));

  return {
    ...data,
    pages,
  };
};

// # Regarding variedHeaders:
// Sometimes an HTTP request header can influence the contents of the HTTP response.
// In those cases, the `Vary` response header should include a list of request headers
// that influenced the response content. Ideally, we'd use that to determine which
// request headers need to be a part of the React Query cacheKey, but React Query wants
// you to provide the cacheKey upfront before the request is ever made. Instead, we're
// maintaining a list here as this is an edge case currently.

type CollectionQueryConfig<
  Path extends ApiPath,
  QueryParams extends keyof EndpointQueryParameters<Path>,
  MemberType extends CollectionQueryData<Path> = CollectionQueryData<Path>,
  Params extends CollectionQueryParams<Path, QueryParams> = CollectionQueryParams<Path, QueryParams>,
> = {
  fetcherName: string;
  path: Path;
  defaultHeaders?: Record<string, string>;
  variedHeaders?: string[];
  query?: Omit<EndpointQueryParameters<Path>, QueryParams>;
  queryParams?: QueryParams[];
  dontThrowOnStatusCodes?: number[];
} & EnhancedUseQueryConfig<MemberType, Params>;

export function makeCollectionQuery<
  Path extends ApiPath,
  QueryParams extends keyof EndpointQueryParameters<Path> = never,
  MemberType extends CollectionQueryData<Path> = CollectionQueryData<Path>,
  Params extends CollectionQueryParams<Path, QueryParams> = CollectionQueryParams<Path, QueryParams>,
>(
  config: CollectionQueryConfig<Path, QueryParams, MemberType, Params>,
): CollectionQueryType<Path, QueryParams, MemberType, Params> {
  const {
    path,
    query,
    queryParams,
    defaultHeaders,
    variedHeaders,
    dontThrowOnStatusCodes = [],
    fetcherName,
    onSuccess: onSuccessConfig,
    onError: onErrorConfig,
    onSettled: onSettledConfig,
    select: selectConfig,
    ...extraConfig
  } = config;

  const route = path;
  const urlPattern = urlPatternFromFetcher(route, query, queryParams);

  registerQuery(fetcherName);

  async function fetchPage(
    requestUrl: string | undefined,
    headers: Record<string, string> | undefined,
    queryKey: QueryKey,
  ): Promise<DojoCollectionPage<MemberType>> {
    if (!requestUrl) {
      return {
        _items: [],
      };
    }

    try {
      const response = await apiFetch<DojoCollectionPage<MemberType>>({
        method: "GET",
        path: requestUrl,
        route,
        headers,
      });

      if (response.success) {
        if (typeof response.data !== "object" || response.data === null || !("_items" in response.data)) {
          throw new InvalidCollectionResponseError({
            path: requestUrl,
            method: "GET",
            url: requestUrl,
            headers: response.headers,
            status: response.status,
            route,
          });
        }

        if (response.data._items?.some((item: unknown) => item === undefined || item === null)) {
          captureException(new Error("Collection response from API includes null/undefined item"), {
            extra: {
              queryKey,
            },
          });
        }
        return response.data;
      }

      const ex = response.error;
      if (ex instanceof APIResponseError) {
        ex.isExpected = dontThrowOnStatusCodes.includes(ex.response.statusCode);

        ex.message = makeApiErrorMessage({
          path: config.path,
          method: "GET",
          response: ex.response,
          name: fetcherName,
        });
      }
      throw ex;
    } catch (ex: unknown) {
      if (!(ex instanceof Error)) {
        throw ex;
      }

      handleSafariSpecialCase(ex, { path: config.path, method: "GET" });

      throw ex;
    }
  }

  // The actual custom hook pre-configured
  // eslint-disable-next-line complexity
  function useInfiniteQueryWrapper(
    params: FullCollectionQueryParams<Params> = {} as Params,
    options: EnhancedUseQueryOptions<MemberType, Params> = {},
  ) {
    const { onSuccess: onSuccessOption, onError: onErrorOption, onSettled: onSettledOption, ...extraOptions } = options;

    const queryKey = useMemo(() => makeQueryKey({ fetcherName, params, variedHeaders }), [params]);

    const awaitingDependencies = params === WAITING_FOR_DEPENDENCIES;
    const isNoop = params === NOOP;
    const firstPageUrl = !isNoop && !awaitingDependencies ? buildUrl({ params, urlPattern }, true) : undefined;
    const headers = !isNoop && !awaitingDependencies ? buildHeaders(defaultHeaders, params[CUSTOM_HEADERS]) : undefined;
    const enabled = !isNoop && !awaitingDependencies && Boolean(firstPageUrl);

    const select = useCallback((data: InfiniteData<DojoCollectionPage<MemberType>> | undefined) => {
      const flatData = flattenInfiniteData(data, fetcherName);
      if (selectConfig && flatData) {
        return selectConfig(flatData);
      }
      return flatData;
    }, []);

    const { queryFn, onSuccess, onError, onSettled } = useQueryScope<DojoCollectionPage<MemberType>, MemberType[]>({
      queryFn: ({ pageParam: pageUrl }) => fetchPage(pageUrl ?? firstPageUrl, headers, queryKey),
      onSuccess: (data) => {
        onSuccessConfig?.(data, params as Params);
      },
      onError: (error) => {
        onErrorConfig?.(error, params as Params);
      },
      onSettled: (data, error) => onSettledConfig?.(data, error, params as Params),
    });

    const result = useInfiniteQuery<DojoCollectionPage<MemberType>, APIResponseError, MemberType[]>({
      queryKey,
      queryFn,
      onSuccess: mergeFns(onSuccess, (data) => onSuccessOption?.(data, params as Params)),
      onError: mergeFns(onError, (error) => onErrorOption?.(error, params as Params)),
      onSettled: mergeFns(onSettled, (data, error) => onSettledOption?.(data, error, params as Params)),
      enabled,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retryOnMount: false,
      staleTime: Infinity,
      retry: false,
      // react-query uses a paged data structure for infinite queries.
      // Select allows us to massage the shape of the data while
      // maintaining the paged data structure in the react-query cache.
      // The select function is run whenever new data is fetched or if
      // the fn reference passed in changes.
      select,
      getNextPageParam: (lastPage) => {
        return lastPage?._links?.next?.href;
      },
      getPreviousPageParam: (firstPage) => {
        const prevPageHref = firstPage?._links?.prev?.href;
        const nextPageHref = firstPage?._links?.next?.href;
        return prevPageHref !== nextPageHref ? prevPageHref : undefined;
      },
      ...extraConfig,
      ...extraOptions,
    });

    // Same failure policy as makeMemberQuery - see the "Failure policy" comment on the equivalent
    // throw in memberQuery.ts for what `dontThrowOnStatusCodes` is and is not the right home for.
    if (result.error && !result.error.isExpected) {
      throw result.error;
    }

    return result;
  }

  useInfiniteQueryWrapper.shouldInvalidateQueries = (
    shouldInvalidateQuery: (
      data: MemberType[] | undefined,
      params: CollectionQueryParams<Path, QueryParams>,
    ) => boolean | null | undefined,
  ): void => {
    const queriesData = queryClient.getQueriesData<InfiniteData<DojoCollectionPage<MemberType>>>({
      queryKey: makeQueryKey({ fetcherName }),
    });
    queriesData.forEach(([queryKey, data]) => {
      const [, params] = queryKey as [string, CollectionQueryParams<Path, QueryParams>];
      if (shouldInvalidateQuery(flattenInfiniteData(data, fetcherName), params)) {
        useInfiniteQueryWrapper.invalidateQueries(params).catch(captureException);
      }
    });
  };

  useInfiniteQueryWrapper.invalidateQueries = async (
    params?: Partial<CollectionQueryParams<Path, QueryParams>>,
    filters?: Omit<InvalidateQueryFilters, "queryKey">,
    options?: InvalidateOptions,
  ): Promise<void> => {
    return queryClient.invalidateQueries({ queryKey: makeQueryKey({ fetcherName, params }), ...filters }, options);
  };

  useInfiniteQueryWrapper.cancelQueries = async (
    params?: Partial<CollectionQueryParams<Path, QueryParams>>,
    filters?: Omit<QueryFilters, "queryKey">,
    options?: CancelOptions,
  ): Promise<void> => {
    return queryClient.cancelQueries({ queryKey: makeQueryKey({ fetcherName, params }), ...filters }, options);
  };

  useInfiniteQueryWrapper.getQueryData = (
    params?: CollectionQueryParams<Path, QueryParams>,
  ): MemberType[] | undefined => {
    const queryKey = makeQueryKey({ fetcherName, params });
    return flattenInfiniteData(
      queryClient.getQueryData<InfiniteData<DojoCollectionPage<MemberType>>>(queryKey),
      fetcherName,
    );
  };

  useInfiniteQueryWrapper.getQueriesData = (
    params?: Partial<CollectionQueryParams<Path, QueryParams>>,
    filters?: Omit<InvalidateQueryFilters, "queryKey">,
  ): [QueryKey, MemberType[] | undefined][] => {
    const queriesData = queryClient.getQueriesData<InfiniteData<DojoCollectionPage<MemberType>>>({
      queryKey: makeQueryKey({ fetcherName, params }),
      ...filters,
    });
    return queriesData.map(([queryKey, data]) => [queryKey, flattenInfiniteData(data, fetcherName)]);
  };

  useInfiniteQueryWrapper.setQueriesData = (
    updater: ((draft: MemberType[]) => MemberType[] | void) | MemberType[] | null,
    params?: Partial<CollectionQueryParams<Path, QueryParams>>,
    filters?: Omit<QueryFilters, "queryKey">,
    options?: SetDataOptions,
  ): void => {
    const searchByQueryKey = makeQueryKey({ fetcherName, params });
    const queriesToUpdate = queryClient.getQueryCache().findAll({ queryKey: searchByQueryKey, ...filters }) as Query<
      InfiniteData<DojoCollectionPage<MemberType>>
    >[];

    const wrappedUpdater = (data: MemberType[]): MemberType[] | null => {
      if (updater instanceof Function) {
        return produce(data, updater);
      }
      return updater;
    };

    addBreadcrumb({
      category: "fetchers",
      message: `Updating ${queriesToUpdate.length} queries`,
      data: { searchByQueryKey, count: queriesToUpdate.length },
    });

    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- TODO: we need to fix this one
    queriesToUpdate.forEach(async (query: Query<InfiniteData<DojoCollectionPage<MemberType>>>) => {
      const { state, queryKey } = query;
      const { data, fetchStatus } = state;
      const flattened = flattenInfiniteData(data, fetcherName);
      if (!flattened) return;

      const reduced = wrappedUpdater(flattened);
      if (isEqual(data, reduced)) return;

      const isFetching = fetchStatus === "fetching";
      if (isFetching) {
        await queryClient.cancelQueries({ queryKey, exact: true });
      }

      const unflattened = unflattenCollection(reduced, data);
      queryClient.setQueriesData({ queryKey, exact: true }, unflattened, options);

      if (isFetching) {
        await queryClient.refetchQueries({ queryKey, exact: true });
      }
    });
  };

  useInfiniteQueryWrapper.prefetch = (params: Params): Promise<void> => {
    const queryKey = makeQueryKey({ fetcherName, params, variedHeaders });
    const firstPageUrl = buildUrl({ params, urlPattern }, true);
    const headers = buildHeaders(defaultHeaders, params[CUSTOM_HEADERS]);

    return queryClient.prefetchInfiniteQuery({
      queryKey,
      queryFn: () => fetchPage(firstPageUrl, headers, queryKey),
      staleTime: Infinity,
    });
  };

  return useInfiniteQueryWrapper;
}
